import FormatCurrency from 'components/Common/FormatCurrency'
import Image from 'components/Common/Image'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import MessagingLabel from 'LuxKit/Label/MessagingLabel'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import { rem } from 'polished'
import styled from 'styled-components'
import type { WeddingGift } from 'weddings/api/weddings'
import WeddingGiftContributionProgress from './WeddingGiftContributionProgress'
import { useCallback } from 'react'
// import WeddingGiftLoyaltyEarnPoints from './WeddingGiftLoyaltyEarnPoints'
import { useIntl } from 'react-intl'
import { getWeddingGiftCategoryLabel } from 'weddings/utils/weddingGiftLabels'

const WeddingGiftContainer = styled(Group)`
  width: ${rem(270)};
`

const ImageWrapper = styled.div`
  position: relative;
`

const LabelWrapper = styled.div`
  position: absolute;
  top: 0;
  inset-inline-start: 0;
  padding: ${rem(12)};
  z-index: 1;
`

interface Props {
  gift: WeddingGift
  onContributeClick?: (gift: WeddingGift) => void
}

function WeddingGiftTile(props: Props) {
  const { gift, onContributeClick } = props
  const intl = useIntl()
  const progress = gift.contributions?.progress ?? 0
  const contributorsCount = gift.contributions?.contributorsCount ?? 0
  const isFullyGifted = progress >= 100

  const handleContributeClick = useCallback(() => {
    onContributeClick?.(gift)
  }, [onContributeClick, gift])

  return (
    <WeddingGiftContainer direction="vertical" gap={12}>
      <ImageWrapper>
        <LabelWrapper>
          {isFullyGifted && <MessagingLabel variant="successLight">
            {intl.formatMessage({ defaultMessage: 'Fully gifted', id: 'qhjlWm656IfJu7X0jlxf', description: 'Status label shown when a wedding gift has been completely funded.' })}
          </MessagingLabel>}
        </LabelWrapper>
        <Image
          id={gift.imageId}
          alt={gift.title}
          width={270}
          height={180}
          objectFit="cover"
          style={{ filter: isFullyGifted ? 'grayscale(100%)' : 'none' }}
        />
      </ImageWrapper>
      <Group direction="vertical" gap={8}>
        <Group direction="vertical" gap={0}>
          <BodyText variant="medium">{getWeddingGiftCategoryLabel(intl, gift.category)}</BodyText>
          <BodyText variant="large" weight="bold" wrap="truncate" lineClamp={2}>{gift.title}</BodyText>
        </Group>
        {/* <Group direction="horizontal" gap={8} fullWidth>
          <WeddingGiftLoyaltyEarnPoints amount={gift.amount} currency={gift.currency} />
        </Group> */}
        <Group direction="horizontal" gap={16} horizontalAlign="space-between">
          <Group direction="vertical" gap={0}>
            <Heading variant="heading5"><FormatCurrency value={gift.amount} currency={gift.currency} /></Heading>
            <Caption variant="large" colour="light-foreground-alt">
              {intl.formatMessage({ defaultMessage: 'Or gift any amount', id: 'WlAh9E08WVJxMQWaylW1', description: 'Caption below a wedding gift price explaining custom contributions.' })}
            </Caption>
          </Group>
          <TextButton kind="secondary" variant="dark" size="medium" disabled={isFullyGifted} onClick={handleContributeClick}>
            {intl.formatMessage({ defaultMessage: 'Contribute', id: '98XGzxeFkj62PgY0NyPy', description: 'Button label for contributing money towards a wedding gift.' })}
          </TextButton>
        </Group>
        <WeddingGiftContributionProgress progress={progress} contributorsCount={contributorsCount} />
      </Group>
    </WeddingGiftContainer>
  )
}

export default WeddingGiftTile
